import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { refreshSession, safeGetSession } from '../supabase/config';
import { supabaseFetch } from '../utils/supabaseHelper';
import * as authService from '../services/authService';

const AuthContext = createContext({});

// Session refresh interval (refresh every 10 minutes of activity)
const SESSION_REFRESH_INTERVAL = 10 * 60 * 1000;

// Minimum gap between focus-triggered session checks (5 minutes)
const FOCUS_DEBOUNCE_INTERVAL = 5 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastRefreshRef = useRef(Date.now());
  const lastFocusCheckRef = useRef(0);
  const userRef = useRef(null);

  // Activity-based session refresh - call this before important operations
  const ensureFreshSession = useCallback(async () => {
    if (!user) return false;

    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshRef.current;

    // If it's been more than 10 minutes since last refresh, refresh proactively
    if (timeSinceLastRefresh > SESSION_REFRESH_INTERVAL) {
      console.log('AuthContext: Proactively refreshing session due to inactivity');
      const success = await refreshSession();
      if (success) {
        lastRefreshRef.current = now;
      }
      return success;
    }
    return true;
  }, [user]);

  // Keep userRef in sync
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Fetch user profile data from database via REST API
  const fetchUserData = async (userId) => {
    try {
      // Try users table
      const users = await supabaseFetch(`users?id=eq.${userId}&select=*&limit=1`);
      let data = users && users.length > 0 ? users[0] : null;

      if (!data) {
        // Try partners table
        const partners = await supabaseFetch(`partners?auth_user_id=eq.${userId}&select=*&limit=1`);
        if (partners && partners.length > 0) {
          data = partners[0];
          if (!data.role) data.role = 'partner';
        }
      }

      setUserData(data);
      return data;
    } catch (err) {
      console.error('Error fetching user data:', err);
      return null;
    }
  };

  // Initialize auth state from localStorage (no SDK needed)
  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;
    let initComplete = false;

    // Safety timeout to prevent infinite loading (5 seconds max)
    timeoutId = setTimeout(() => {
      if (isMounted && !initComplete) {
        console.warn('AuthContext: Initialization timed out after 5s, clearing loading state');
        setLoading(false);
      }
    }, 5000);

    const initializeAuth = async () => {
      try {
        console.log('AuthContext: Initializing auth state...');

        // Read session from localStorage
        const { data, error: sessionError } = await safeGetSession();
        const session = data?.session;

        if (sessionError) {
          console.error('Error getting session:', sessionError);
          if (isMounted) {
            initComplete = true;
            setLoading(false);
          }
          return;
        }

        if (session?.user && isMounted) {
          console.log('AuthContext: Found existing session for user:', session.user.id);

          // Validate session by fetching user profile from database via REST
          let profileData = null;

          try {
            const users = await supabaseFetch(`users?id=eq.${session.user.id}&select=*&limit=1`);
            profileData = users && users.length > 0 ? users[0] : null;

            if (!profileData) {
              const partners = await supabaseFetch(`partners?auth_user_id=eq.${session.user.id}&select=*&limit=1`);
              if (partners && partners.length > 0) {
                profileData = partners[0];
                if (!profileData.role) profileData.role = 'partner';
              }
            }
          } catch (e) {
            console.error('AuthContext: Exception fetching profile:', e);
          }

          if (!profileData) {
            // Session exists but user profile not found - stale session
            console.warn('AuthContext: User profile not found, clearing session...');
            authService.clearAuthCache();
            if (isMounted) {
              setUser(null);
              setUserData(null);
              initComplete = true;
              setLoading(false);
              console.log('AuthContext: Initialization complete - profile not found');
            }
            return;
          }

          // Valid session and profile
          if (isMounted) {
            console.log('AuthContext: Session validated, user role:', profileData.role);
            setUser(session.user);
            setUserData(profileData);
            initComplete = true;
            setLoading(false);
            console.log('AuthContext: Initialization complete - user authenticated');
          }
        } else {
          console.log('AuthContext: No existing session found');
          if (isMounted) {
            initComplete = true;
            setLoading(false);
            console.log('AuthContext: Initialization complete - no session');
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        if (isMounted) {
          initComplete = true;
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Window Focus Listener for proactive session refresh (REST-based)
    const handleWindowFocus = async () => {
      if (document.visibilityState !== 'visible' || !userRef.current) return;

      const now = Date.now();
      if (now - lastFocusCheckRef.current < FOCUS_DEBOUNCE_INTERVAL) return;

      lastFocusCheckRef.current = now;

      const timeSinceLastRefresh = now - lastRefreshRef.current;
      if (timeSinceLastRefresh > SESSION_REFRESH_INTERVAL) {
        console.log('AuthContext: Window focused, refreshing session (idle for', Math.round(timeSinceLastRefresh / 60000), 'min)');
        const success = await refreshSession();
        if (success) {
          lastRefreshRef.current = now;
        }
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleWindowFocus);

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleWindowFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auth methods
  const signupCustomer = async (email, password, name, phone) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.customerSignup(email, password, name, phone);
      if (result.success) {
        setUser(result.user);
        await fetchUserData(result.user.id);
      } else {
        setError(result.error);
      }
      return result;
    } finally {
      setLoading(false);
    }
  };

  const loginCustomer = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.customerLogin(email, password);
      if (result.success) {
        setUser(result.user);
        setUserData(result.userData);
      } else {
        setError(result.error);
      }
      return result;
    } finally {
      setLoading(false);
    }
  };

  const loginEmployee = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.employeeLogin(email, password);
      if (result.success) {
        setUser(result.user);
        setUserData(result.userData);
      } else {
        setError(result.error);
      }
      return result;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      // Clear user state first for immediate UI feedback
      setUser(null);
      setUserData(null);

      const result = await authService.logout();
      if (!result.success) {
        console.warn('Logout had an error:', result.error);
      }
      return result;
    } catch (e) {
      console.error('Logout exception:', e);
      return { success: true };
    }
  };

  const refreshUserData = async () => {
    if (user) {
      return await fetchUserData(user.id);
    }
    return null;
  };

  // Helper computed values
  const isAuthenticated = !!user;
  const isCustomer = userData?.role === 'customer';
  const isEmployee = userData?.role === 'employee';
  const isAdmin = userData?.role === 'admin';
  const isPartner = userData?.role === 'partner';
  const isActive = userData?.status === 'active';

  const value = {
    user,
    userData,
    loading,
    error,
    isAuthenticated,
    isCustomer,
    isEmployee,
    isAdmin,
    isPartner,
    isActive,
    signupCustomer,
    loginCustomer,
    loginEmployee,
    signOut,
    refreshUserData,
    ensureFreshSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
